import React from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import VisibilitySensor from 'react-visibility-sensor';
import { withStyles } from '@mui/styles';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import AccountCircleIcon from '@mui/icons-material/AccountCircleOutlined';
import Badge from '@mui/material/Badge';
// import SearchBar from './SearchBar';
import { checkIfAdmin } from '../../utils/adminUsers';
import logoIcon from '../../images/logoIconDesktop.png';
import logoText from '../../images/logoText.png';
import ThemeToggleButton from './ThemeToggleButton';
import {
  loggedInType,
  classesType,
  handleLogInType,
  handleSignUpType,
  handleLogOutType,
  authType,
  toggleDesktopMenuType,
  desktopMenuAnchorType,
  newConvoMsgCountType,
  isHeaderDesktopVisibleType,
  handleCloseMenuType
} from '../../types';

const styles = theme => ({
  root: {
    flexGrow: 1
  },
  appbar: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary
  },
  toolbar: {
    // allows the different elements on the header to wrap on smaller screens
    // without this the desktop header gets cut off if it is accidentally rendered on a mobile screen
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap'
  },
  logo: {
    flex: 1,
    padding: '0 1rem',
    '&:hover': {
      cursor: 'pointer'
    },
    '&:active': {
      cursor: 'pointer'
    },
    display: 'flex', // make the icon and the text into a single row
    alignItems: 'center'
  },
  // the logo art is dark, so flip it to light on the dark header
  logoIcon: {
    height: '1.5rem',
    ...(theme.palette.mode === 'dark' && { filter: 'brightness(0) invert(1)' })
  },
  logoText: {
    height: '2rem',
    ...(theme.palette.mode === 'dark' && { filter: 'brightness(0) invert(1)' })
  },
  desktopMenuButton: {
    color: theme.palette.text.primary
  },
  loginButton: { marginRight: '0.5rem' },
  signupButton: {
    marginRight: '2rem' // add some space to the right of logged out buttons
  }
});

const HeaderDesktop = props => {
  const {
    classes,
    loggedIn,
    handleCloseMenu,
    handleLogIn,
    handleSignUp,
    handleLogOut,
    auth,
    toggleDesktopMenu,
    desktopMenuAnchor,
    newConvoMsgCount,
    isHeaderDesktopVisible
  } = props;

  const renderNewConvoMsgCount = buttonText => {
    if (newConvoMsgCount && newConvoMsgCount !== 0) {
      return (
        <Badge badgeContent={newConvoMsgCount} color="primary">
          {buttonText}
        </Badge>
      );
    }
    return buttonText;
  };

  // if the user is logged In then render Profile, Conversations, Logout
  // if the user is logged Out then render Log In, Sign Up
  const renderButtons = () => {
    if (loggedIn) {
      return (
        <div className="LoggedInDesktopMenuItems">
          <Button
            component={Link}
            to="/conversations"
            onClick={handleCloseMenu}
            className={classes.desktopMenuButton}
          >
            {renderNewConvoMsgCount('Conversations')}
          </Button>
          <Button
            onClick={toggleDesktopMenu}
            aria-label="Menu"
            aria-owns={desktopMenuAnchor ? 'simple-menu' : null}
            aria-haspopup="true"
            className={classes.desktopMenuButton}
            style={{ color: 'inherit' }}
          >
            <AccountCircleIcon style={{ color: 'inherit' }} />
            {auth.currentUser.displayName}
          </Button>
          <Menu
            id="simple-menu"
            anchorEl={desktopMenuAnchor}
            open={Boolean(desktopMenuAnchor)}
            onClose={toggleDesktopMenu}
            disableAutoFocusItem
          >
            <MenuItem component={Link} to="/profile" onClick={handleCloseMenu}>
              Profile
            </MenuItem>
            <MenuItem component={Link} to="/settings" onClick={handleCloseMenu}>
              Settings
            </MenuItem>
            {auth &&
              auth.currentUser &&
              auth.currentUser.displayName &&
              checkIfAdmin(auth.currentUser.displayName) && (
                <MenuItem
                  component={Link}
                  to="/admin"
                  onClick={handleCloseMenu}
                >
                  Admin
                </MenuItem>
              )}
            <MenuItem onClick={handleLogOut}>Logout</MenuItem>
          </Menu>
        </div>
      );
    }
    return (
      <div className="LoggedOutDesktopMenuItems">
        <Button
          onClick={handleLogIn}
          className={classNames(classes.desktopMenuButton, classes.loginButton)}
        >
          Log In
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSignUp}
          className={classNames(
            classes.desktopMenuButton,
            classes.signupButton
          )}
        >
          Sign Up
        </Button>
      </div>
    );
  };

  return (
    <VisibilitySensor
      classNAme={classes.root}
      partialVisibility
      onChange={isVisible => {
        isHeaderDesktopVisible(isVisible);
      }}
    >
      <AppBar position="static" className={classes.appbar} elevation={0}>
        <Toolbar className={classes.toolbar}>
          <Link to="/" className={classes.logo}>
            <img
              alt="EroPenPal Logo Icon"
              src={logoIcon}
              className={classes.logoIcon}
              width="26.838px"
              height="24px"
            />
            <img
              alt="EroPenPal Logo Text"
              src={logoText}
              className={classes.logoText}
              width="135.838px"
              height="32px"
            />
          </Link>
          <Button
            component={Link}
            to="/create"
            onClick={handleCloseMenu}
            className={classes.desktopMenuButton}
          >
            Create A Prompt
          </Button>
          <ThemeToggleButton />
          {renderButtons()}
        </Toolbar>
      </AppBar>
    </VisibilitySensor>
  );
};

HeaderDesktop.propTypes = {
  loggedIn: loggedInType.isRequired,
  classes: classesType.isRequired,
  handleLogIn: handleLogInType,
  handleSignUp: handleSignUpType,
  handleLogOut: handleLogOutType,
  auth: authType,
  toggleDesktopMenu: toggleDesktopMenuType.isRequired,
  desktopMenuAnchor: desktopMenuAnchorType,
  newConvoMsgCount: newConvoMsgCountType,
  isHeaderDesktopVisible: isHeaderDesktopVisibleType.isRequired,
  handleCloseMenu: handleCloseMenuType.isRequired
};

HeaderDesktop.defaultProps = {
  handleLogIn: null,
  handleSignUp: null,
  handleLogOut: null,
  auth: null,
  desktopMenuAnchor: null,
  newConvoMsgCount: 0
};

export default withStyles(styles)(HeaderDesktop);
